import * as d3 from 'd3';
import _ from 'underscore';

import asm from 'assembly/Asm';
import {assertAndContinue} from 'Asserts';
import {Sequence} from 'biojs/Sequence';
import {getTickColor, getTicks} from 'molbio/maps/circular/CircularAxisUtil';
import {AxisLayout, CircularLayout} from 'molbio/maps/circular/CircularLayout';
import Display from 'molbio/maps/circular/Display';
import {HomologyAwareFragmentResultData} from 'molbio/maps/circular/HomologyUtil';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import {RingView} from 'molbio/maps/circular/RingView';
import * as sequtil from 'molbio/SeqUtil';
import {getBaseWidthStripeParams} from 'molbio/visualization/FragmentVisualizationUtil';

interface AxisViewOptions extends RingComponentOptions<AxisLayout> {
  circularLayout: CircularLayout;
}

class CircularAxisView extends RingView<AxisLayout> {
  _sequence!: Sequence;
  circularLayout!: CircularLayout;

  static initClass(): void {
    this.prototype.className = 'axis';
  }

  override redrawPlasmid(): boolean {
    return true;
  }

  computeInnerRadius(outerRadius: number): number {
    return outerRadius - 22;
  }

  override triggerRedraw(): void {
    this.model.trigger('invalidateLayers');
    super.triggerRedraw();
  }

  override initialize(options: AxisViewOptions): void {
    super.initialize(options);
    this.circularLayout = options.circularLayout;
    this._sequence = this.model.get('sequence').get('_sequence');
    this.listenTo(this._sequence, 'change:finalized change:fragmentResults', () => {
      this.update();
    });
    assertAndContinue(this._sequence.startIndex() === 1, 'CircularAxisView rendering with startIndex != 1');
  }

  override render(): this {
    const root = d3.select(this.el);
    root.append('g').classed('axis-labels', true);
    this.update();
    return this;
  }

  getPiecesData(): Array<HomologyAwareFragmentResultData> | null {
    if (!this._sequence.finalized()) {
      return asm
        .of(this._sequence)
        .getFragmentResultPieces(this._sequence)
        .map(([color, start, end]) => ({
          pieceStartAngle: this.basePairToAngle(start),
          pieceEndAngle: this.basePairToAngle(end),
          color,
        }));
    }
    return null;
  }

  override redraw(): void {
    if (this.layout.get('visible')) {
      this.$el.show();
    } else {
      this.$el.hide();
      this.layout.set('innerRadius', this.getOuterRadius());
      return;
    }

    this.layout.set('innerRadius', this.computeInnerRadius(this.getOuterRadius()));

    const tickSize = this.layout.get('tickSize');
    let ticks = getTicks({
      fullCircleView: this.model.get('fullCircleView'),
      leftBP: this.model.get('leftBP'),
      rightBP: this.model.get('rightBP'),
      sequenceLength: this.model.get('sequence').length(),
    });

    // eslint-disable-next-line no-restricted-syntax
    const hasOne = _.indexOf(ticks, 1, true) !== -1;
    // @ts-expect-error: this looks like a noop?
    ticks = _.union(ticks, 1);

    // Refresh data, and recompute layers.`
    const root = d3.select(this.el);
    const radius = this.getOuterRadius();
    const label = root.select('.axis-labels').selectAll('.axis-label').data(ticks);
    const enter = label.enter().append('g').classed('axis-label', true);
    const enterText = enter
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-weight', 900)
      .attr('font-size', this.layout.get('type').fontSize);
    enterText.append('svg:textPath').classed('text-path', true);
    enter.append('path').attr('stroke', 'none').attr('stroke-width', 1).attr('fill', 'none');
    enter.append('line').attr('stroke', 'black').attr('shape-rendering', 'geometricPrecision');
    label.exit().remove();

    const pathArc = d3.lineRadial().curve(d3.curveBasis);

    const ref = this;
    const piecesData = this.getPiecesData();
    const {useBaseWidthStripe} = getBaseWidthStripeParams(
      ref._sequence.length(),
      ref.getOuterRadius(),
      (basePair) => ref.basePairToAngle(basePair)
    );
    label.each(function (d: number) {
      const standardAngle = ref.basePairToAngle(sequtil.displayToModel(d, ref._sequence.startIndex()));

      const pt1 = Display.polar(
        d === 1 ? radius - tickSize - ref.layout.get('type').height() : radius - tickSize,
        standardAngle
      );
      const pt2 = Display.polar(d === 1 ? radius : radius, standardAngle);

      const path = d3.select(this).select('path');

      let pathArcRadius: number;
      let pathData;
      const anglePadding = Math.min(ref.layout.get('type').width(String(d).length * 2) / radius, Math.PI);
      if (Math.PI / 2 < standardAngle && standardAngle < (3 * Math.PI) / 2) {
        pathData = d3.range(standardAngle + anglePadding, standardAngle - anglePadding, -anglePadding / 4);
        pathArcRadius = radius - tickSize;
        pathArc.radius(pathArcRadius); // + ref.layout.get('type').height() - 2)
      } else {
        pathData = d3.range(standardAngle - anglePadding, standardAngle + anglePadding, anglePadding / 4);
        pathArcRadius = radius - tickSize - ref.layout.get('type').height() + 2;
        pathArc.radius(pathArcRadius);
      }

      path
        .datum(pathData.map((angle) => [angle, pathArcRadius]) satisfies Array<[number, number]>)
        .attr('d', pathArc)
        .attr('id', `axis-${d}-${ref.cid}`);

      d3.select(this)
        .select('text')
        .select('.text-path')
        .attr('xlink:href', `#axis-${d}-${ref.cid}`)
        .attr('startOffset', pathArcRadius * anglePadding)
        .text(d === 1 && !hasOne ? '' : d);
      d3.select(this)
        .select('line')
        .attr('stroke-width', d === 1 ? 4 : 2)
        .attr('stroke', (d: number) =>
          getTickColor({
            fragmentResults: ref._sequence.fragmentResults().models,
            piecesData,
            standardAngle,
            startIndex: ref._sequence.startIndex(),
            tick: d,
            useBaseWidthStripe,
          })
        )
        .attr('shape-rendering', 'geometricPrecision')
        .attr('x1', pt1.x)
        .attr('y1', pt1.y)
        .attr('x2', pt2.x)
        .attr('y2', pt2.y);
    });
  }

  clickAction(e: Event, d: unknown): void {}

  dblClickAction(e: Event, d: unknown): void {}
}

CircularAxisView.initClass();

export default CircularAxisView;
